import {
  CheckCircleOutlined,
  DeleteOutlined,
  HelpOutlined,
  VisibilitySharp,
} from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

import type { Order, OrderStatus } from "../../types/order";

export interface OrderRowActionMenuProps {
  anchorEl: HTMLElement | null;
  status?: OrderStatus;
  onClose: () => void;
  onExited?: () => void;
  onView: () => void;
  onApprove: () => void;
  onRequestMoreDetails: () => void;
  onDelete: () => void;
}

/** Statuses that are terminal for status updates */
const TERMINAL_STATUSES: OrderStatus[] = ["CANCELLED", "CREATED"];

const menuItemSx = {
  minHeight: 40,
  px: 3,
  py: 1,
  gap: 1,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.4,
  color: "#25272C",
  "&:hover": { bgcolor: "#EDEEF1" },
} as const;

const iconSx = { minWidth: 16, color: "#25272C" } as const;
const textSx = { primary: { sx: { fontSize: 14, lineHeight: 1.4 } } } as const;

export default function OrderRowActionMenu({
  anchorEl,
  status,
  onClose,
  onExited,
  onView,
  onApprove,
  onRequestMoreDetails,
  onDelete,
}: OrderRowActionMenuProps) {
  const isTerminal = TERMINAL_STATUSES.includes(status as OrderStatus);
  const canApprove = !isTerminal;
  const canRequestMore = !isTerminal && status !== "REQUEST_MORE_INFO";
  const canDelete = status === "CANCELLED" || !isTerminal;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={{
        transition: { onExited },
        paper: {
          sx: {
            minWidth: 220,
            borderRadius: "8px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
            overflow: "hidden",
          },
        },
        list: { sx: { p: 0 } },
      }}
    >
      <MenuItem onClick={onView} sx={menuItemSx}>
        <ListItemIcon sx={iconSx}>
          <VisibilitySharp sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText primary="View" slotProps={textSx} />
      </MenuItem>
      {canApprove && (
        <MenuItem onClick={onApprove} sx={menuItemSx}>
          <ListItemIcon sx={iconSx}>
            <CheckCircleOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Order Created" slotProps={textSx} />
        </MenuItem>
      )}
      {canRequestMore && (
        <MenuItem onClick={onRequestMoreDetails} sx={menuItemSx}>
          <ListItemIcon sx={iconSx}>
            <HelpOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Request More Details" slotProps={textSx} />
        </MenuItem>
      )}
      {canDelete && (
        <MenuItem onClick={onDelete} sx={menuItemSx}>
          <ListItemIcon sx={iconSx}>
            <DeleteOutlined sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="Delete" slotProps={textSx} />
        </MenuItem>
      )}
    </Menu>
  );
}

// Re-export Order type for convenience (used by parent to type actionRow)
export type { Order };
